import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, InputNumber, Space, message, Input, Select } from 'antd';
import { createCoupon, getCoupons, markCouponUsed } from '../services/api';

const CouponManagement = () => {
  const [maxShopsFilter, setMaxShopsFilter] = useState(null);
  const [expiryDaysFilter, setExpiryDaysFilter] = useState(null);
  const [coupons, setCoupons] = React.useState([]);
  const [maxShops, setMaxShops] = useState(1);
  const [expiryDays, setExpiryDays] = useState(1);
  const [createCount, setCreateCount] = useState(1);
  const [createFeature, setCreateFeature] = useState('all');
  const [searchCode, setSearchCode] = useState('');
  const [searchUserName, setUserName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const loadCoupons = async () => {
      try {
        const response = await getCoupons();
        setCoupons(response?.coupons || []);
      } catch (error) {
        console.error('Error loading coupons:', error);
        setCoupons([]);
      }
    };
    loadCoupons();
  }, []);

  const fetchCoupons = useCallback(async () => {
    try {
      const params = {
        maxShops: maxShopsFilter,
        expiryDays: expiryDaysFilter,
        isUsed: filterStatus === 'used' ? true : filterStatus === 'unused' ? false : undefined,
      };

      if (searchCode) {
        params.code = searchCode;
      }

      if (searchUserName) {
        params.username = searchUserName;
      }

      if (searchEmail) {
        params.email = searchEmail;
      }

      const res = await getCoupons(params);
      setCoupons(res?.coupons || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setCoupons([]);
    }
  }, [searchCode, filterStatus, maxShopsFilter, expiryDaysFilter, searchUserName, searchEmail]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleDelete = async (id) => {
    try {
      const coupon = coupons.find((c) => c._id === id);
      if (!coupon) return;

      await markCouponUsed(coupon.code);
      message.success('激活码已注销');
      await fetchCoupons();
    } catch (error) {
      message.error(`注销失败: ${error.message}`);
    }
  };

  const handleCreate = async () => {
    try {
      const res = await createCoupon({
        maxShops,
        expiryDays,
        count: createCount,
        feature: createFeature,
      });

      if (res?.success) {
        console.log('创建成功:', res.data);
      } else {
        throw new Error(res.data?.message || res?.message || '创建失败');
      }

      message.success(`成功创建 ${createCount} 个激活码: ${res.data.coupons.join(', ')}`);
      console.log('所有激活码:', res.data.coupons.join(', '));
      fetchCoupons();
    } catch (error) {
      console.error('创建激活码详细错误:', error);
      message.error(`创建失败: ${error.message}`);
    }
  };

  const columns = [
    { title: 'id', dataIndex: '_id', key: '_id' },
    { title: '激活码', dataIndex: 'code', key: 'code' },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => new Date(text).toLocaleString(),
    },
    { title: '会员等级', dataIndex: 'level', key: 'level' },
    { title: '最大店铺数', dataIndex: 'maxShops', key: 'maxShops' },
    { title: '有效期(天)', dataIndex: 'expiryDays', key: 'expiryDays' },
    { title: '功能', dataIndex: 'feature', key: 'feature' },
    { title: '使用人', dataIndex: 'usedUserName', key: 'usedUserName' },
    {
      title: '使用时间',
      dataIndex: 'usedAt',
      key: 'usedAt',
      render: (text) => (text ? new Date(text).toLocaleString() : '未使用'),
    },
    {
      title: '到期时间',
      dataIndex: 'expiryAt',
      key: 'expiryAt',
      render: (text, record) => (record.isUsed ? (text ? new Date(text).toLocaleString() : '-') : '未使用'),
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => (record.isUsed ? '已使用' : '未使用'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button danger onClick={() => handleDelete(record._id)} disabled={record.isUsed}>
          注销
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space size="large" style={{ marginBottom: 24, width: '100%' }}>
        <div>
          <span>店铺个数: </span>
          <InputNumber min={1} value={maxShops} onChange={setMaxShops} />
        </div>
        <div>
          <span>有效天数: </span>
          <InputNumber min={1} value={expiryDays} onChange={setExpiryDays} />
        </div>
        <div>
          <span>创建个数: </span>
          <InputNumber min={1} value={createCount} onChange={setCreateCount} />
        </div>
        <div>
          <span>功能: </span>
          <Select
            value={createFeature}
            onChange={setCreateFeature}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: 'all' },
              { value: 'getFaildSku', label: 'getFaildSku' },
            ]}
          />
        </div>
        <Button type="primary" onClick={handleCreate}>
          创建激活码
        </Button>
      </Space>

      <Space size="middle" style={{ marginBottom: 24, width: '100%' }}>
        <Input.Search
          placeholder="输入使用人查询"
          value={searchUserName}
          onChange={(e) => setUserName(e.target.value)}
          onSearch={fetchCoupons}
          style={{ width: 200 }}
        />

        <Input.Search
          placeholder="输入邮箱查询"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          onSearch={fetchCoupons}
          style={{ width: 200 }}
        />
      </Space>

      <Space size="middle" style={{ marginBottom: 24, width: '100%' }}>
        <Input.Search
          placeholder="输入激活码查询"
          value={searchCode}
          onChange={(e) => setSearchCode(e.target.value)}
          onSearch={fetchCoupons}
          style={{ width: 200 }}
        />
        <InputNumber
          placeholder="店铺数量筛选"
          min={1}
          onChange={setMaxShopsFilter}
          style={{ width: 120 }}
        />
        <InputNumber
          placeholder="有效期筛选(天)"
          min={1}
          onChange={setExpiryDaysFilter}
          style={{ width: 120 }}
        />
        <Select
          value={filterStatus}
          onChange={setFilterStatus}
          style={{ width: 120 }}
          options={[
            { value: 'all', label: '全部状态' },
            { value: 'used', label: '已使用' },
            { value: 'unused', label: '未使用' },
          ]}
        />
        <Button type="primary" onClick={fetchCoupons}>
          查询
        </Button>
      </Space>

      <Table dataSource={coupons} columns={columns} rowKey="_id" />
    </div>
  );
};

export default CouponManagement;
